/**
 * Authentication Module
 * Handles user authentication and profile management
 */

import { state, saveAuth, clearAuth } from './state.js';
import { apiUrl, ensureAccessToken, type ApiConfig } from './api';
import { deriveSuperadmin, resolveRoleEnv } from './roles.js';
import type { PortalUser } from '../types';
import { createLogger } from '../../shared/logger.js';
import {
  checkAuthStatus,
  login as pkceLogin,
  logout as pkceLogout,
} from '../../services/webapp-pkce-auth';

const log = createLogger('portal:auth');

const AUTH_DEBUG = false; // set true for verbose auth diagnostics

type AccountFragment = PortalUser | Record<string, unknown> | null;

type AuthResultPayload = {
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number | string;
  idTokenPayload?: Auth0IdTokenPayload;
  user?: PortalUser | null;
  account?: PortalUser | null;
};

type Auth0IdTokenPayload = Record<string, unknown> & {
  sub?: string;
  name?: string;
  email?: string;
  nickname?: string;
  role?: string;
  isSuperUser?: boolean | string;
  ['https://lotlingo.com/role']?: string;
  ['https://lotlingo.com/isSuperUser']?: boolean | string;
};

interface LogoutOptions {
  AUTH0_DOMAIN: string;
  AUTH0_CLIENT_ID: string;
  clearSyncIndicatorDelay?: () => void;
  cancelPolling?: () => void;
  cancelCreditPolling?: () => void;
  clearCreditCache?: () => void;
  resetRemoteSnapshot?: () => void;
  disconnectRealTime?: (reason?: string) => void;
  hideAuthError?: () => void;
  render?: () => void;
  applyModeBanner?: () => void;
  save?: () => void;
  logoutReturnUrl?: string;
  useAuth0Logout?: boolean;
}

function logAuthFailure(label: string, response: Response) {
  if (!AUTH_DEBUG) return;
  (async () => {
    let body = '';
    try {
      body = await response.text();
    } catch {
      // Ignore
    }
    log.warn(`${label} failed ${response.status} ${String(body).slice(0, 200)}`);
  })();
}

function readSuperUserFromTokenClaims(token: string | null | undefined): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) {
      return false;
    }
    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '==='.slice((normalized.length + 3) % 4);
    const decoded = JSON.parse(atob(padded)) as Record<string, unknown>;
    const direct = decoded.isSuperUser;
    const custom = decoded['https://lotlingo.com/isSuperUser'];
    const legacy = decoded['https://lotlingo.com/claims/is_superuser'];
    if (direct === true || custom === true || legacy === true) {
      return true;
    }
    if (typeof direct === 'string' && direct.toLowerCase() === 'true') {
      return true;
    }
    if (typeof custom === 'string' && custom.toLowerCase() === 'true') {
      return true;
    }
    if (typeof legacy === 'string' && legacy.toLowerCase() === 'true') {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Apply user data to state
 */
function normalizeIdentityValue(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

export function applyUser(user: PortalUser | null) {
  console.warn(
    '[superadmin-diag] applyUser called with isSuperUser:',
    user?.isSuperUser,
    '| email:',
    user?.email
  );
  if (!user) {
    state.user = null;
    return;
  }
  const previous = state.user;
  if (!previous) {
    state.user = { ...user };
    return;
  }

  const merged: PortalUser = { ...previous, ...user };
  (['displayName', 'name', 'email'] as Array<keyof PortalUser>).forEach(key => {
    const incoming = normalizeIdentityValue(user[key]);
    if (incoming) {
      merged[key] = incoming;
      return;
    }
    const existing = normalizeIdentityValue(previous[key]);
    if (existing) {
      merged[key] = existing;
    } else {
      delete merged[key];
    }
  });

  state.user = merged;
}

/**
 * Parse account fragment from URL hash
 */
export function parseAccountFragment(accountParam: string | null): AccountFragment {
  if (!accountParam) return null;
  try {
    const decoded = atob(accountParam);
    return JSON.parse(decoded) as PortalUser;
  } catch {
    return null;
  }
}

/**
 * Parse fragment tokens from URL hash
 * SECURITY: Only accepts tokens from Auth0 redirect - validates state parameter before use
 */
export function parseFragmentTokens(): boolean {
  if (!window.location.hash) return false;
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const at = params.get('access_token');
  const rt = params.get('refresh_token');
  const exp = Number(params.get('expires_in'));
  const accountParam = params.get('account');

  if (at) {
    state.extAccessToken = at;
    state.extRefreshToken = rt || null;
    state.extExpiresAt =
      Number.isFinite(exp) && exp > 0
        ? Date.now() + exp * 1000 - 30000
        : Date.now() + 2592000 * 1000;
    const account = parseAccountFragment(accountParam);
    if (account) {
      applyUser(account as PortalUser);
    }
    saveAuth({
      provider: 'auth0',
      tokenType: 'Bearer',
      accessToken: state.extAccessToken,
      refreshToken: state.extRefreshToken ?? undefined,
      expiresAt: state.extExpiresAt,
      user: state.user ?? undefined,
    });
    // Clear hash
    history.replaceState({}, document.title, window.location.pathname + window.location.search);
    return true;
  }
  return false;
}

/**
 * Get user profile from API
 */
export async function getUserProfile(cfg?: ApiConfig | null): Promise<PortalUser | null> {
  try {
    let token = await ensureAccessToken(cfg ?? null);
    if (!token) return null;
    const tokenSuperUser = readSuperUserFromTokenClaims(token);

    const endpoint = apiUrl(cfg ?? null, 'user/profile');
    const response = await fetch(endpoint, {
      headers: { Authorization: 'Bearer ' + token },
    });

    if (response.status === 401) {
      state.extAccessToken = null;
      token = await ensureAccessToken(cfg ?? null, true);
      if (!token) return null;
      const retryResponse = await fetch(endpoint, {
        headers: { Authorization: 'Bearer ' + token },
      });
      if (!retryResponse.ok) {
        logAuthFailure('profile (retry)', retryResponse);
        return null;
      }
      const retryProfile = (await retryResponse.json()) as PortalUser;
      if (retryProfile && tokenSuperUser) {
        retryProfile.isSuperUser = Boolean(retryProfile.isSuperUser) || tokenSuperUser;
      }
      return retryProfile;
    }
    if (!response.ok) {
      logAuthFailure('profile', response);
      return null;
    }
    const profile = (await response.json()) as PortalUser;
    if (profile && tokenSuperUser) {
      profile.isSuperUser = Boolean(profile.isSuperUser) || tokenSuperUser;
    }
    return profile;
  } catch (err) {
    log.error('Error fetching user profile:', err);
    return null;
  }
}

/**
 * Upsert user profile to API
 */
export async function upsertUserProfile(
  cfg: ApiConfig | null,
  profileData: Record<string, unknown>
): Promise<boolean> {
  try {
    let token = await ensureAccessToken(cfg ?? null);
    if (!token) return false;

    const endpoint = apiUrl(cfg ?? null, 'user/profile');
    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
      },
      body: JSON.stringify(profileData),
    });

    if (response.status === 401) {
      state.extAccessToken = null;
      token = await ensureAccessToken(cfg ?? null, true);
      if (!token) return false;
      const retryResponse = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(profileData),
      });
      if (!retryResponse.ok) {
        logAuthFailure('upsert profile (retry)', retryResponse);
      }
      return retryResponse.ok;
    }
    if (!response.ok) {
      logAuthFailure('upsert profile', response);
    }
    return response.ok;
  } catch (err) {
    log.error('Error upserting user profile:', err);
    return false;
  }
}

/**
 * Check if user has insufficient credits
 */
export function hasInsufficientCredits(): boolean {
  const credits = state.creditBalance?.creditsRemaining;
  if (typeof credits !== 'number') {
    return false;
  }
  return credits <= 0;
}

/**
 * Show app (hide auth card)
 */
export function showApp(authCardEl?: HTMLElement | null, appEl?: HTMLElement | null) {
  if (authCardEl) authCardEl.hidden = true;
  if (appEl) appEl.hidden = false;
}

/**
 * Show auth card (hide app)
 */
export function showAuthCard(authCardEl?: HTMLElement | null, appEl?: HTMLElement | null) {
  if (authCardEl) authCardEl.hidden = false;
  if (appEl) appEl.hidden = true;
}

/**
 * Logout user
 */
export function logout() {
  state.mode = 'local';
  state.extAccessToken = null;
  state.extRefreshToken = null;
  state.extExpiresAt = 0;
  state.user = null;
  clearAuth();
}

/**
 * Perform complete logout and redirect through Auth0 logout endpoint
 */
export function performLogout(options: LogoutOptions) {
  const {
    clearSyncIndicatorDelay,
    cancelPolling,
    cancelCreditPolling,
    clearCreditCache,
    resetRemoteSnapshot,
    disconnectRealTime,
    hideAuthError,
    render,
    applyModeBanner,
    logoutReturnUrl,
    save,
  } = options;

  state.extAccessToken = null;
  state.extRefreshToken = null;
  state.extExpiresAt = 0;
  state.remoteError = '';
  state.isFetchingRemote = false;
  state.creditBalance = null;
  state.creditLastFetchedAt = 0;
  state.lots = [];
  state.page = 1;
  try {
    localStorage.removeItem('lotlingo.portal.lots.v1');
  } catch {
    // Ignore storage errors
  }

  if (clearSyncIndicatorDelay) clearSyncIndicatorDelay();
  if (cancelPolling) cancelPolling();
  if (cancelCreditPolling) cancelCreditPolling();
  if (clearCreditCache) clearCreditCache();
  if (disconnectRealTime) disconnectRealTime('logout');
  applyUser(null);
  state.mode = 'local';
  if (resetRemoteSnapshot) resetRemoteSnapshot();
  clearAuth();
  if (hideAuthError) hideAuthError();
  if (render) render();
  if (applyModeBanner) applyModeBanner();
  if (save) save();

  try {
    localStorage.setItem('lotlingo.portal.logoutFlag.v1', Date.now().toString());
  } catch {
    // Ignore storage errors
  }

  // Auth0 SPA-SDK (PKCE) logout flow:
  // pkceLogout() clears the locally persisted PKCE auth, clears the Auth0 SPA SDK
  // cache, and redirects to the Auth0 /v2/logout endpoint with the resolved
  // returnTo URL. We deliberately end only the Auth0/LotLingo session (no
  // 'federated' logout) so the user is not signed out of their upstream identity
  // provider (e.g. Google).
  //
  // Auto re-auth is prevented by the 'lotlingo.portal.logoutFlag.v1' flag set
  // above (consumed by checkLogoutFlag on the next load) combined with the Auth0
  // session being ended, so no SWA cookie "skip" window is needed.
  const defaultReturn = window.location.origin + '/?logged_out=true';
  const resolvedReturnUrl =
    typeof logoutReturnUrl === 'string' && logoutReturnUrl.trim().length
      ? logoutReturnUrl.trim()
      : defaultReturn;

  void pkceLogout(resolvedReturnUrl);
}

/**
 * Normalize incoming user object from various sources
 */
export function normalizeUser(input: unknown): PortalUser | null {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  const safeString = (val: unknown): string | undefined =>
    typeof val === 'string' && val.trim().length > 0 ? val : undefined;

  return {
    sub: safeString(obj.sub) ?? safeString(obj.userId) ?? safeString(obj.id),
    name:
      safeString(obj.displayName) ??
      safeString(obj.name) ??
      safeString(obj.nickname) ??
      safeString(obj.email) ??
      'Guest',
    email: safeString(obj.email),
    company: safeString(obj.company),
    displayName: safeString(obj.displayName),
    userId: safeString(obj.userId) ?? safeString(obj.id),
    isSuperUser: Boolean(obj.isSuperUser),
    raw: undefined,
  } as PortalUser;
}

/**
 * Apply Auth0 / SWA token exchange result to state and user model
 */
export function applyAuthResult(authResult?: AuthResultPayload | null): boolean {
  if (!authResult || !authResult.accessToken) {
    return false;
  }
  const expiresIn = Number(authResult.expiresIn);
  state.extAccessToken = authResult.accessToken;
  state.extRefreshToken =
    typeof authResult.refreshToken === 'string' ? authResult.refreshToken : state.extRefreshToken;
  state.extExpiresAt =
    Number.isFinite(expiresIn) && expiresIn > 0
      ? Date.now() + expiresIn * 1000 - 30000
      : Date.now() + 2592000 * 1000;
  const payload: Auth0IdTokenPayload = authResult.idTokenPayload || {};
  const customRole =
    typeof payload['https://lotlingo.com/role'] === 'string'
      ? payload['https://lotlingo.com/role']
      : undefined;
  const customSuper = payload['https://lotlingo.com/isSuperUser'];
  let payloadSuper: boolean | string | undefined;
  if (typeof payload.isSuperUser === 'boolean') {
    payloadSuper = payload.isSuperUser;
  } else if (typeof payload.isSuperUser === 'string') {
    payloadSuper = payload.isSuperUser;
  } else if (typeof customSuper === 'boolean' || typeof customSuper === 'string') {
    payloadSuper = customSuper;
  }
  const candidateUser: PortalUser = (authResult.user as PortalUser | null) ||
    (authResult.account as PortalUser | null) || {
      name: (payload.name || payload.email || '') as string,
      email: payload.email as string | undefined,
      sub: payload.sub as string | undefined,
      role: typeof payload.role === 'string' ? payload.role : customRole,
      isSuperUser: Boolean(payloadSuper),
    };
  candidateUser.isSuperUser =
    Boolean(candidateUser.isSuperUser) ||
    deriveSuperadmin(candidateUser, payload, resolveRoleEnv());
  applyUser(candidateUser);
  const mergedUser = state.user ?? candidateUser;
  saveAuth({
    provider: 'auth0',
    tokenType: 'Bearer',
    accessToken: state.extAccessToken,
    refreshToken: state.extRefreshToken ?? undefined,
    expiresAt: state.extExpiresAt,
    user: mergedUser,
  });
  return true;
}

/**
 * Start the Auth0 SPA-SDK (PKCE) login redirect.
 * The Auth0 client handles the authorize redirect; on return,
 * exchangePkceAuth() completes the flow and mints app tokens.
 */
export function redirectLogin() {
  try {
    void pkceLogin();
  } catch (err) {
    log.error('Failed to start login redirect', err);
  }
}

/**
 * Complete the Auth0 SPA-SDK (PKCE) flow and return tokens in the portal's
 * AuthResultPayload shape.
 *
 * checkAuthStatus() handles every entry case: returning from the Auth0
 * authorize redirect (code/state in the query string), an existing Auth0
 * session, the shared dashboard cookie, and previously persisted PKCE auth.
 * Internally it exchanges the Auth0 tokens with POST /extensions/auth/token-exchange.
 */
export async function exchangePkceAuth(_cfg?: ApiConfig | null): Promise<AuthResultPayload | null> {
  const startTime = Date.now();
  try {
    log.info('[auth:pkce] Starting Auth0 PKCE auth check');
    // Pass the runtime apiBaseUrl (from config.json) so the token-exchange POST
    // targets the correct environment's API. The PKCE service's build-time
    // default points at production, which would CORS-reject non-prod hosts.
    const runtimeApiBase =
      _cfg && typeof (_cfg as { apiBaseUrl?: unknown }).apiBaseUrl === 'string'
        ? (_cfg as { apiBaseUrl?: string }).apiBaseUrl
        : undefined;
    const stored = await checkAuthStatus(runtimeApiBase);
    if (!stored || !stored.accessToken) {
      log.info('[auth:pkce] No Auth0 session/token available');
      return null;
    }

    const expiresIn =
      typeof stored.expiresAt === 'number' && stored.expiresAt > Date.now()
        ? Math.max(1, Math.round((stored.expiresAt - Date.now()) / 1000))
        : undefined;
    const account = stored.account ?? {};
    const user: PortalUser = {
      sub:
        (typeof account.subject === 'string' && account.subject) ||
        (typeof account.userId === 'string' ? account.userId : undefined),
      userId: typeof account.userId === 'string' ? account.userId : undefined,
      email: typeof account.email === 'string' ? account.email : undefined,
      name: typeof account.name === 'string' ? account.name : undefined,
      isSuperUser: Boolean(account.isSuperUser),
    } as PortalUser;

    log.info(`[auth:pkce] Auth0 PKCE auth completed in ${Date.now() - startTime}ms`);
    return {
      accessToken: stored.accessToken,
      refreshToken: stored.refreshToken,
      expiresIn,
      idTokenPayload: stored.idTokenClaims as Auth0IdTokenPayload | undefined,
      user,
    };
  } catch (err) {
    log.warn(`[auth:pkce] Auth0 PKCE auth failed after ${Date.now() - startTime}ms`, err);
    return null;
  }
}
